import { MelodyLib, latencyCalibrationLexiconId } from '@tunasong/music-lib'
import { Button } from '@tunasong/ui-lib'
import type { FC } from 'react'
import { PitchInputMeter, type PitchMeterState } from '../pitch-training/pitch-input-meter.js'
import { useVocalCopy } from '../pitch-training/use-vocal-copy.js'
import { TheoryGlyphs } from '../theory-copy/theory-glyphs.js'
import styles from './latency-calibration-panel.module.css'

/**
 * The eight-click tap test (§6.3).
 *
 * The instruction ("tap on each click") is a UI label - it tells the user what to do, it does not
 * judge what they did - so it is written here. The two sentences that ARE verdicts about the
 * measurement, `timing-calibrate-first` and `timing-calibrated`, come from the lexicon.
 *
 * The panel never opens a device. Where the taps come from is the host's business; this only knows
 * whether to draw something to press.
 *
 * Spec: docs/superpowers/specs/2026-09-08-melody-plugin-design.md §6.3, §10
 */

/** Where the taps arrive from. Only `keyboard` needs a control drawn for it. */
export type LatencyTapSource = 'audio' | 'midi' | 'keyboard'

const SOURCE_PROMPT: Record<LatencyTapSource, string> = {
  audio: 'Sing or play a short note on each click — eight of them.',
  midi: 'Play one note on each click — eight of them.',
  keyboard: 'Tap the button (or the space bar) on each click — eight of them.',
}

export interface LatencyCalibrationPanelProps {
  tapSource: LatencyTapSource
  /** The input this measurement will be filed under. */
  deviceLabel: string | null
  /** True while the clicks are sounding. */
  running: boolean
  tapCount: number
  /** The finished measurement, or null - see `attempted` for which kind of null. */
  result: MelodyLib.LatencyCalibration | null
  /** True once a run has finished, so "not started" can be told from "too few taps". */
  attempted: boolean
  onStart: () => void
  onTap: () => void
  onDone: () => void
  onCancel: () => void
  /**
   * Switch to tapping a key. Offered when the mode's own input is the tap source, because not
   * every player can put a clean note on eight consecutive clicks - and a calibration that cannot
   * be completed leaves the timing score switched off (§6.3).
   */
  onUseKeyboard?: () => void
  /** Live input, in audio mode. This is where a dead microphone is first met. */
  meter?: PitchMeterState
}

export const LatencyCalibrationPanel: FC<LatencyCalibrationPanelProps> = props => {
  const {
    tapSource,
    deviceLabel,
    running,
    tapCount,
    result,
    attempted,
    onStart,
    onTap,
    onDone,
    onCancel,
    onUseKeyboard,
    meter,
  } = props

  const copy = useVocalCopy(latencyCalibrationLexiconId(result === null ? null : result.inputLatencyMs), {
    ms: result === null ? undefined : Math.round(result.inputLatencyMs),
  })

  /*
   * The spread the median was taken over. A calibration can be confidently wrong - a run where the
   * user tapped a beat behind produces a tidy median 600 ms out - and the only thing that lets a
   * person notice is seeing the numbers it came from.
   */
  const spread =
    result === null || result.samples.length === 0
      ? null
      : `${result.samples.length} taps, ${Math.round(Math.min(...result.samples))}–${Math.round(
          Math.max(...result.samples)
        )} ms`

  const failed = attempted && result === null

  return (
    <section className={styles.panel} aria-label="Latency calibration">
      <h2 className={styles.title}>Measure your setup&apos;s delay</h2>

      {copy ? (
        <p className={styles.intro}>
          <TheoryGlyphs>{copy.body}</TheoryGlyphs>
        </p>
      ) : null}

      <p className={styles.prompt}>{SOURCE_PROMPT[tapSource]}</p>
      {deviceLabel ? <p className={styles.device}>{`Measuring: ${deviceLabel}`}</p> : null}

      <div className={styles.status} role="status" aria-label="Taps">
        {/*
         * An audio run cannot count live: its taps are found by segmenting the frames once the run
         * has finished, which is the Pitch Trainer's already-tested pipeline rather than a second
         * onset detector written here. So it shows the meter instead of a count it does not have.
         */}
        {running ? (tapSource === 'audio' ? 'Listening…' : `${tapCount} of ${MelodyLib.latencyClickCount}`) : 'Ready when you are'}
      </div>

      {meter ? (
        <PitchInputMeter frame={meter.frame} noiseFloorRms={meter.noiseFloorRms} deviceLabel={meter.deviceLabel} />
      ) : null}

      {spread ? <p className={styles.spread}>{spread}</p> : null}

      {failed ? (
        <p className={styles.problem}>
          We only caught a few of those. Let&apos;s run it again — tap on every click.
        </p>
      ) : null}

      <div className={styles.actions}>
        {tapSource === 'keyboard' ? (
          <Button
            variant="contained"
            color="primary"
            /*
             * Disabled rather than hidden while idle: the control the user is about to need should
             * not appear from nowhere the instant the clicks start.
             */
            disabled={!running}
            onClick={onTap}
            onKeyDown={event => {
              if (event.key === ' ') {
                event.preventDefault()
                onTap()
              }
            }}
          >
            Tap
          </Button>
        ) : null}

        {result !== null ? (
          <Button variant="contained" color="primary" onClick={onDone}>
            Done
          </Button>
        ) : (
          <Button variant={tapSource === 'keyboard' ? 'text' : 'contained'} disabled={running} onClick={onStart}>
            {failed ? 'Try again' : 'Start'}
          </Button>
        )}

        {onUseKeyboard && tapSource !== 'keyboard' && !running ? (
          <Button variant="text" onClick={onUseKeyboard}>
            Tap with a key instead
          </Button>
        ) : null}

        <Button variant="text" onClick={onCancel}>
          Cancel
        </Button>
      </div>
    </section>
  )
}
